import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { StatsCard } from '../components/StatsCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SkeletonStats, SkeletonCard } from '../components/ui/skeleton';
import { StatusPieChart } from '../components/ChartComponents';
import { ExpiryCalendar } from '../components/ExpiryCalendar';
import { certificatesAPI, logsAPI } from '../lib/api';
import { calculateDaysRemaining, getCertificateStatus } from '../lib/utils';
import { Shield, AlertTriangle, CheckCircle, Clock, Activity, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { id } from 'date-fns/locale';

// WITA Timezone (Waktu Indonesia Tengah - UTC+8)
const WITA_TIMEZONE = 'Asia/Makassar';

export function Overview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);


  useEffect(() => {
    loadData();
    // Auto refresh setiap 60 detik
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      // Prepare logs parameters with date filters
      const logsParams = {
        page: 1,
        per_page: 5
      };


      const [certsResponse, logsResponse] = await Promise.all([
        certificatesAPI.getAll(),
        logsAPI.getLogs(logsParams)
      ]);

      setCertificates(certsResponse.data);

      // Handle different API response structures for logs
      let logs = [];
      if (logsResponse.data) {
        if (Array.isArray(logsResponse.data)) {
          logs = logsResponse.data;
        } else if (logsResponse.data.logs && Array.isArray(logsResponse.data.logs)) {
          logs = logsResponse.data.logs;
        } else if (logsResponse.data.data && Array.isArray(logsResponse.data.data)) {
          logs = logsResponse.data.data;
        }
      }

      setRecentLogs(logs);
    } catch (error) {
      toast.error('Gagal memuat data');
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleViewAllLogs = () => {
    navigate('/logs');
  };

  /**
   * Format timestamp dengan timezone WITA
   * @param {string|Date} timestamp - Timestamp dari API
   * @returns {string} Formatted date string in WITA
   */
  const formatTimestampWITA = (timestamp) => {
    try {
      if (!timestamp) return '-';

      // Parse timestamp ke Date object
      const date = new Date(timestamp);

      // Convert ke WITA timezone using toZonedTime
      const witaDate = toZonedTime(date, WITA_TIMEZONE);

      // Format: "03 Des 2025, 14:30 WITA"
      return format(witaDate, 'dd MMM yyyy, HH:mm', { locale: id }) + ' WITA';
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return timestamp;
    }
  };

  /**
   * Format date range display dengan WITA
   */
  const formatDateRangeWITA = (start, end) => {
    try {
      const startDate = toZonedTime(new Date(start), WITA_TIMEZONE);
      const endDate = toZonedTime(new Date(end), WITA_TIMEZONE);

      return `${format(startDate, 'dd MMM', { locale: id })} - ${format(endDate, 'dd MMM yyyy', { locale: id })}`;
    } catch (error) {
      return `${start} - ${end}`;
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-8 w-64 bg-muted animate-pulse rounded mb-2" />
          <div className="h-4 w-96 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <SkeletonStats />
          <SkeletonStats />
          <SkeletonStats />
          <SkeletonStats />
        </div>
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  // Hitung statistik
  const totalCerts = certificates.length;
  const validCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'valid';
  }).length;

  const criticalCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'critical';
  }).length;

  const warningCerts = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'warning';
  }).length;

  const expiringSoon = warningCerts + criticalCerts;

  const expired = certificates.filter(cert => {
    const days = calculateDaysRemaining(cert.expiration_date);
    return getCertificateStatus(days) === 'expired';
  }).length;

  // Generate mock trend data untuk sparklines (dalam real app, ini dari API)
  const generateTrendData = (value) => {
    return Array.from({ length: 7 }, (_, i) => ({
      value: Math.max(0, value + Math.floor(Math.random() * 3) - 1)
    }));
  };

  // Data untuk pie chart
  const statusDistribution = {
    valid: validCerts,
    warning: warningCerts,
    critical: criticalCerts,
    expired: expired
  };

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants}>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Ringkasan Dashboard</h2>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Monitor status sertifikat SSL untuk semua domain
        </p>
      </motion.div>

      {/* Statistics Cards */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Sertifikat"
          value={totalCerts}
          description="Jumlah total sertifikat"
          icon={Shield}
          colorClass="text-blue-600"
          trend={0}
          trendData={generateTrendData(totalCerts)}
          index={0}
        />
        <StatsCard
          title="Sertifikat Valid"
          value={validCerts}
          description="Sertifikat masih valid"
          icon={CheckCircle}
          colorClass="text-green-600"
          trend={2}
          trendData={generateTrendData(validCerts)}
          index={1}
        />
        <StatsCard
          title="Akan Kedaluwarsa"
          value={expiringSoon}
          description="Akan expire dalam 30 hari"
          icon={Clock}
          colorClass="text-orange-600"
          trend={expiringSoon > 0 ? -1 : 0}
          trendData={generateTrendData(expiringSoon)}
          index={2}
        />
        <StatsCard
          title="Kedaluwarsa"
          value={expired}
          description="Sertifikat sudah kedaluwarsa"
          icon={AlertTriangle}
          colorClass="text-red-600"
          trend={expired > 0 ? -1 : 0}
          trendData={generateTrendData(expired)}
          index={3}
        />
      </div>

      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 lg:items-stretch">
        {/* Certificates Expiring Soon */}
        <motion.div variants={itemVariants} className="flex">
          <Card className="flex flex-col w-full min-h-[400px]">
            <CardHeader className="flex-shrink-0">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                Sertifikat Akan Kedaluwarsa
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <div className="space-y-3">
                {certificates
                  .filter(cert => {
                    const days = calculateDaysRemaining(cert.expiration_date);
                    const status = getCertificateStatus(days);
                    return status === 'warning' || status === 'critical';
                  })
                  .slice(0, 5)
                  .map((cert, index) => {
                    const days = calculateDaysRemaining(cert.expiration_date);
                    return (
                      <motion.div
                        key={cert.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, x: 4 }}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors gap-4 cursor-pointer"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm sm:text-base">{cert.domain}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">{cert.server_source}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <Badge className={`${days <= 7 ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'} text-xs sm:text-sm whitespace-nowrap`}>
                            {days} hari
                          </Badge>
                        </div>
                      </motion.div>
                    );
                  })}
                {expiringSoon === 0 && (
                  <motion.div
                    className="text-center py-8 text-muted-foreground"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                    <p className="text-sm sm:text-base">Semua sertifikat dalam kondisi baik</p>
                  </motion.div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity with Date Filter - WITH WITA TIMEZONE */}
        <motion.div variants={itemVariants} className="flex">
          <Card className="flex flex-col w-full min-h-[400px]">
            <CardHeader className="flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Activity className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    Aktivitas Terkini
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Menampilkan 5 aktivitas terbaru
                  </CardDescription>
                </div>

                {/* View All Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleViewAllLogs}
                  className="gap-2 whitespace-nowrap flex-shrink-0"
                >
                  <span>Lihat Semua</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <div className="space-y-3">
                {recentLogs.length > 0 ? (
                  recentLogs.slice(0, 5).map((log, index) => (
                    <motion.div
                      key={log.id || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      whileHover={{ scale: 1.01, x: 4 }}
                      className="flex items-start gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <motion.div
                        className={`mt-0.5 h-2 w-2 rounded-full flex-shrink-0 ${log.level === 'ERROR' || log.level === 'CRITICAL' ? 'bg-red-500' :
                          log.level === 'WARNING' ? 'bg-orange-500' :
                            'bg-blue-500'
                          }`}
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.7, 1, 0.7]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.2
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium break-words">
                          {log.message || log.action || log.description || 'Aktivitas tidak diketahui'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestampWITA(log.timestamp || log.created_at || log.date)}
                        </p>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    className="text-center py-8 text-muted-foreground"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Activity className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm sm:text-base">Belum ada aktivitas</p>
                  </motion.div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 lg:items-stretch">
        {/* Status Distribution Pie Chart */}
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
          className="flex"
        >
          <div className="w-full">
            <StatusPieChart data={statusDistribution} />
          </div>
        </motion.div>

        {/* Expiry Calendar */}
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
          className="flex"
        >
          <div className="w-full">
            <ExpiryCalendar certificates={certificates} />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}